import { CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useReducer, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { useAuth } from "../../hooks/useAuth";
import { useEventQuery } from "../../hooks/events/useEventQuery";
import { general } from "../../locales/general";
import EventTable from "./EventTable";
import TypesTable from "./TypesTable";
import { Chip } from "../../helpers/general";
import { cardStyle } from "../Places/PlacesTable";
import { controlStyle } from "../../helpers/constants";

const PAGE_SIZE = 10;
const ACCENT = "#f0813f";

const TABS = [
  { key: 0, label: "Événements" },
  { key: 1, label: "Types d'événements" },
] as const;

export default function Events() {
  const [activeTab, setActiveTab] = useState<0 | 1>(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const {
    events,
    fetchingEvents,
    mutating,
    removeEvent,
    searchHandler,
    totalEvents,
    totalPages,
    setCurrentPage,
    currentPage,
  } = useEventQuery();

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      removeEvent(id);
      dispatch({ type: DELETE_ACTIONS.REMOVE, id });
    });
  };

  const goOrganize = () => {
    queryClient.invalidateQueries("getEventsOrganize");
    navigate("/events/organize");
  };

  return (
    <section>
      {/* Title + count + add */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="fs-4 fw-bold mb-0">Événements</h5>
          {activeTab === 0 && totalEvents !== undefined && (
            <Chip tone="warning">
              {totalEvents} {totalEvents > 1 ? "Événements" : "Événement"}
            </Chip>
          )}
        </div>

        {activeTab === 0 && (
          <Link
            to="/events/new"
            className="btn btn-primary text-white rounded-pill px-4"
          >
            + Nouvel événement
          </Link>
        )}
      </div>

      {/* Tabs (admin only) */}
      {isAdmin && (
        <div className="d-flex gap-2 mb-3">
          {TABS.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className="border-0 rounded-pill"
                style={{
                  padding: "8px 18px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: active ? "#fff" : "#444",
                  backgroundColor: active ? ACCENT : "#fff",
                  boxShadow: active
                    ? "0 4px 12px rgba(240,129,63,0.35)"
                    : "0 1px 3px rgba(0,0,0,0.08)",
                  transition: "all .15s ease",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {activeTab === 0 && (
        <>
          {/* Toolbar */}
          <div
            style={{ ...cardStyle, padding: "12px 16px" }}
            className="d-flex flex-wrap align-items-center gap-2 mb-3"
          >
            <div
              className="d-flex align-items-center gap-2 px-3 flex-grow-1"
              style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
            >
              <MagnifyingGlassIcon color="gray" width={18} />
              <input
                type="text"
                placeholder="Rechercher un événement"
                className="border-0 w-100 bg-transparent"
                style={{ outline: "none", fontSize: 13 }}
                onChange={(e) => searchHandler(e.target.value.toLowerCase())}
              />
            </div>

            {isAdmin && (
              <button
                type="button"
                onClick={goOrganize}
                className="btn btn-outline-primary rounded-pill px-3"
                style={{ height: 38 }}
              >
                Organiser les événements
              </button>
            )}

            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={mutating || selectedCount === 0}
              className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
            >
              {mutating ? (
                <>
                  <CSpinner size="sm" /> {general.fr.wait}
                </>
              ) : (
                <>
                  <DeleteIcon fill={colors.primary} /> Supprimer
                  {selectedCount > 0 && ` (${selectedCount})`}
                </>
              )}
            </button>
          </div>

          {/* Table */}
          <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
            <RenderTable
              loading={fetchingEvents}
              render={() =>
                events?.length > 0 ? (
                  <EventTable
                    events={events as Events[]}
                    eventsToBeDeletedList={dispatch}
                  />
                ) : (
                  <p className="text-center text-muted py-5 mb-0">
                    {general.fr.noData}
                  </p>
                )
              }
            />
          </div>

          <Pagination
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            siblingCount={1}
            totalCount={totalPages}
            pageSize={PAGE_SIZE}
          />
        </>
      )}

      {activeTab === 1 && (
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <TypesTable />
        </div>
      )}
    </section>
  );
}
